import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Search, ChevronRight, ChevronDown, FileText, Folder, FolderOpen,
    ExternalLink, Send, Bot, User, X, PanelLeft, Download, History,
    Menu, HelpCircle, Info as InfoIcon, MessageSquare, Cpu, CheckCircle,
    MousePointer2, Sparkles, Zap, BookOpen, List, Database
} from 'lucide-react';
import Chart from 'chart.js/auto';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import AdminDashboard from './AdminDashboard';

const API_BASE = process.env.REACT_APP_API_BASE_URL ||
    (window.location.hostname === 'chatbot.kiast.or.kr'
        ? ''
        : `http://${window.location.hostname}:8000`);

const ReactIcon = () => (
    <svg viewBox="-11.5 -10.23174 23 20.46348" width="14" height="14">
        <circle cx="0" cy="0" r="2.05" fill="#61dafb" />
        <g stroke="#61dafb" strokeWidth="1" fill="none">
            <ellipse rx="11" ry="4.2" />
            <ellipse rx="11" ry="4.2" transform="rotate(60)" />
            <ellipse rx="11" ry="4.2" transform="rotate(120)" />
        </g>
    </svg>
);

const OpenAIIcon = () => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" className="text-emerald-600">
        <path d="M22.28 11.455c.316-.494.444-1.09.362-1.678-.137-1.026-.85-1.873-1.83-2.17-.183-.054-.37-.083-.56-.088v-.294c0-1.05-.623-2.007-1.583-2.435-.18-.082-.37-.148-.564-.198a2.722 2.722 0 0 0-1.155-.03c-.563.123-1.07.417-1.46.843l-.337.37c-.36-.188-.748-.31-1.15-.36a2.71 2.71 0 0 0-1.673.364c-.495.316-1.09.444-1.678.362a2.72 2.72 0 0 0-2.17 1.83c-.054.183-.083.37-.088.56h-.294A2.721 2.721 0 0 0 5.17 10.1c-.082.18-.148.37-.198.564a2.722 2.722 0 0 0-.03 1.155c.123.563.417 1.07.843 1.46l.37.337c-.188.36-.31.748-.36 1.15a2.71 2.71 0 0 0 .364 1.673c.316.495.444 1.09.362 1.678a2.72 2.72 0 0 0 1.83 2.17c.183.054.37.083.56.088v.294a2.721 2.721 0 0 0 2.435 1.583c.18.082.37.148.564.198.19.049.385.074.582.074.193 0 .385-.027.573-.078.563-.123 1.07-.417 1.46-.843l.337-.37c.36.188.748.31 1.15.36a2.71 2.71 0 0 0 1.673-.364c.495-.316 1.09-.444 1.678-.362a2.72 2.72 0 0 0 2.17-1.83c.054-.183.083-.37.088-.56h.294a2.721 2.721 0 0 0 1.583-2.435c.082-.18.148-.37.198-.564a2.722 2.722 0 0 0 .03-1.155 2.722 2.722 0 0 0-.843-1.46l-.37-.337c.188-.36.31-.748.36-1.15a2.71 2.71 0 0 0-.364-1.673zM12 12V8.307c0-.012.005-.02.015-.02l3.193 1.844c.01.006.01.02 0 .025l-3.208 1.844zm-3.208-1.844c.01-.005.02-.005.025 0l3.183 1.844-3.208 1.844c-.01.006-.02-.005-.02-.015V10.156zm1.144 5.55l3.208-1.844-3.208-1.844zm2.064 3.594l-3.208-1.844v-3.688l3.208 1.844z" />
    </svg>
);

const JSIcon = () => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="#F7DF1E">
        <path d="M0 0h24v24H0V0zm22.034 18.258c-.224-1.128-1.072-1.914-2.224-2.355l-.54-.213c-.582-.231-.865-.367-.865-.715 0-.312.27-.545.702-.545.457 0 .725.191.923.541l1.71-.916c-.452-.832-1.199-1.313-2.507-1.313-1.815 0-2.881 1.094-2.881 2.515 0 1.397.922 2.15 2.151 2.63l.539.214c.732.29.988.526.988.919 0 .433-.367.702-.915.702-.64 0-1.057-.298-1.332-.82l-1.704 1.011c.494.99 1.403 1.554 2.96 1.554 1.807 0 3.064-1.171 3.064-2.839zM11.514 14.898h2.007v4.842c0 1.277-.601 2.119-2.201 2.119-.851 0-1.182-.235-1.503-.609l.93-.941c.213.272.338.338.538.338.226 0 .341-.159.341-.413v-5.336z" />
    </svg>
);

// --- [Utility] 라벨 텍스트 추출 ---
const getPlainText = (label) => {
    if (!label) return "";
    if (typeof label === 'string') return label;
    if (Array.isArray(label)) return label.map(getPlainText).join('');
    if (label?.props?.children) return getPlainText(label.props.children);
    return "";
};

// --- [Utility] 시간 포맷터 ---
const formatTime = (date) => {
    try {
        return new Date(date).toLocaleTimeString('ko-KR', {
            hour: '2-digit', minute: '2-digit', hour12: true
        });
    } catch (e) { return ""; }
};

// --- [Utility] 메시지 전처리 (태그 -> 가상 HTTP 링크 변환) ---
const preprocessMessage = (text) => {
    if (typeof text !== 'string') return text || "";
    // ⟪규정명|조항⟫ -> [규정명 조항](http://action_search?reg=...&clause=...)
    return text.replace(/⟪(.*?)\|(.*?)⟫/g, (match, reg, art) => {
        const cleanReg = reg.trim();
        const cleanArt = art.trim();
        const label = `${cleanReg} ${cleanArt}`;
        const params = new URLSearchParams();
        params.append('reg', cleanReg);
        params.append('clause', cleanArt);
        return `[${label}](http://action_search?${params.toString()})`;
    });
};

// --- [Component] 초기 공지 화면 (Integrated Profile Style) ---
const WelcomeScreen = ({ sysInfo, isSystemReady }) => {
    return (
        <div className="w-full h-full bg-slate-50/30 p-0 sm:p-4 select-none flex flex-col sm:justify-center overflow-hidden">

            {/* [추가] 커서 깜빡임 애니메이션 스타일 정의 */}
            <style>{`
                @keyframes terminal-blink {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0; }
                }
                .animate-terminal-cursor {
                    animation: terminal-blink 1s step-end infinite;
                }
            `}</style>

            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 2.0 }}
                className="relative bg-white sm:rounded-2xl shadow-xl shadow-slate-200/60 border-b sm:border border-slate-100 p-4 sm:p-6 max-w-md w-full mx-auto flex flex-col min-h-0 flex-1 sm:flex-none sm:h-auto sm:max-h-full"
            >
                {/* 상단 장식 라인 */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-slate-800 to-blue-600"></div>

                {/* Integrated Header: Icon + Title */}
                <div className="flex items-center gap-4 mb-3 sm:mb-5 pb-3 sm:pb-5 border-b border-slate-50 shrink-0">
                    <div className="relative shrink-0">
                        <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center shadow-md">
                            <Bot size={24} className="text-white" strokeWidth={1.5} />
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-emerald-500 border-[3px] border-white rounded-full"></div>
                    </div>

                    <div className="flex-1">
                        <div className="flex items-center justify-between">
                            <h2 className="text-sm font-extrabold text-slate-800">KIAST AI 규정 비서</h2>
                            <span className="px-1.5 py-0.5 rounded bg-blue-50 text-[10px] font-bold text-blue-600 tracking-tight">BOT {sysInfo.version || 'v0.4'}</span>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                            <Sparkles size={10} />
                            <span>언제나 정확한 답변을 위해 노력합니다</span>
                        </p>
                    </div>
                </div>

                {/* Markdown Content */}
                <div className="text-slate-600 text-[13px] leading-relaxed break-keep min-h-0 flex-1 overflow-y-auto pr-2 scrollbar-thin">
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                            h3: ({ node, ...props }) => <div className="font-bold text-slate-800 mb-2" {...props} />,
                            strong: ({ node, ...props }) => <span className="font-bold text-blue-700 bg-blue-50 px-1 rounded mx-0.5" {...props} />,
                            ul: ({ node, ...props }) => <ul className="space-y-2 mb-3" {...props} />,
                            li: ({ node, ...props }) => (
                                <li className="flex items-start gap-2.5" {...props}>
                                    <div className="w-1 h-1 rounded-full bg-slate-300 mt-2 shrink-0" />
                                    <span>{props.children}</span>
                                </li>
                            ),
                            em: ({ node, ...props }) => (
                                <div className="block mt-4 pt-3 border-t border-slate-50 text-[11px] text-slate-400 bg-slate-50/50 p-2.5 rounded-lg not-italic">
                                    💡 {props.children}
                                </div>
                            ),
                            p: ({ node, ...props }) => <>{props.children}</>
                        }}
                    >
                        {sysInfo.notice || "공지사항을 불러오는 중입니다..."}
                    </ReactMarkdown>
                </div>

                {/* Footer Stats - [수정됨] System Ready + Blinking Cursor */}
                <div className="mt-3 sm:mt-5 pt-2 flex justify-between items-center text-[12px] font-medium shrink-0 border-t border-slate-50 sm:border-none">
                    <div className={isSystemReady === false ? "flex items-center text-red-500" : "flex items-center text-slate-400"}>
                        <span>{isSystemReady === false ? "시스템 업데이트/정비중" : "System Ready"}</span>
                        {/* 깜빡이는 언더바 커서 */}
                        <span className="animate-terminal-cursor font-bold ml-0.5">_</span>
                    </div>

                    <div className="flex gap-3 text-slate-400">
                        <span>Total: <b className="text-slate-700">{sysInfo.total}</b></span>
                        <span>Active: <b className="text-blue-600">{sysInfo.current}</b></span>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

// --- [Component] 이용 가이드 모달 ---
const SystemGuide = ({ onClose, sysInfo }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [doNotShowAgain, setDoNotShowAgain] = useState(false);
    const isHiddenSaved = localStorage.getItem('kiast_hide_guide') === 'true';

    // [추가] 각 스텝 요소(카드)를 참조하기 위한 Ref 배열
    const stepRefs = useRef([]);

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveStep((prev) => (prev + 1) % 4);
        }, 2500);
        return () => clearInterval(timer);
    }, []);

    // [추가] 활성 스텝이 변경될 때 모바일(세로모드)이면 해당 위치로 스크롤 이동
    useEffect(() => {
        // Tailwind의 md 브레이크포인트(768px) 미만일 때만 동작 (세로 모드)
        if (window.innerWidth < 768) {
            const currentEl = stepRefs.current[activeStep];
            if (currentEl) {
                currentEl.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center', // 화면 중앙에 오도록 정렬
                    inline: 'nearest'
                });
            }
        }
    }, [activeStep]);

    const handleClose = () => {
        if (doNotShowAgain) {
            localStorage.setItem('kiast_hide_guide', 'true');
        }
        onClose();
    };

    const steps = [
        {
            title: "질문하기",
            subtitle: "자연어 질의",
            desc: "평소 사용하는 말투로 편하게 질문하세요.",
            example: "제주 2박3일 출장비는 얼마인가요?",
            tech: { name: "React UI", label: "INTERFACE", icon: <ReactIcon /> },
            icon: <MessageSquare className="text-blue-500" size={20} />,
            color: "bg-blue-50",
            accent: "bg-blue-500",
            borderColor: "border-blue-200"
        },
        {
            title: "AI 분석",
            subtitle: "실시간 탐색",
            desc: `기술원내 ${sysInfo?.current || '123'}개의 규정을 즉시 검토합니다.`,
            tech: { name: "RAG Engine", label: "VECTOR DB", icon: <Database size={14} className="text-indigo-500" /> },
            icon: (
                <div className="relative">
                    <Search className="text-indigo-600" size={20} />
                    <motion.div animate={{ opacity: [0, 1, 0], scale: [0.8, 1.2, 0.8] }} transition={{ repeat: Infinity, duration: 1.5 }} className="absolute -top-1.5 -right-1.5 text-yellow-400">
                        <Sparkles size={10} />
                    </motion.div>
                </div>
            ),
            color: "bg-indigo-50",
            accent: "bg-indigo-500",
            borderColor: "border-indigo-200"
        },
        {
            title: "스마트 답변",
            subtitle: "근거 기반 생성",
            desc: "실제 현행 규정을 바탕으로 최적의 AI 엔진이 답변합니다.",
            tech: { name: "GPT-6.1-Terra", label: "OPENAI LLM", icon: <OpenAIIcon /> },
            icon: <Cpu className="text-purple-500" size={20} />,
            color: "bg-purple-50",
            accent: "bg-purple-500",
            borderColor: "border-purple-200"
        },
        {
            title: "규정 확인",
            subtitle: "딥링크 이동",
            desc: "해당 조항으로 즉시 이동합니다.",
            tech: { name: "딥링크 API", label: "JAVASCRIPT", icon: <JSIcon /> },
            icon: <BookOpen className="text-emerald-500" size={20} />,
            color: "bg-emerald-50",
            accent: "bg-emerald-500",
            borderColor: "border-emerald-200"
        }
    ];

    return (
        <div className="relative w-full max-w-6xl bg-slate-50 rounded-[32px] overflow-hidden shadow-2xl p-6 md:p-10 flex flex-col items-center justify-center max-h-[95vh]">
            <button onClick={handleClose} className="absolute top-6 right-6 z-50 p-2 bg-white hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-900 transition-all shadow-sm">
                <X size={24} />
            </button>

            <div className="max-w-2xl w-full text-center mb-6 shrink-0">
                <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-slate-200 rounded-full shadow-sm mb-3">
                    <Zap size={12} className="text-blue-500 fill-blue-500" />
                    <span className="text-[9px] font-bold text-slate-600 uppercase tracking-widest">SMART AI SYSTEM GUIDE</span>
                </motion.div>
                <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-1 leading-tight">
                    똑똑한 <span className="text-[0.6em] text-slate-400 font-normal opacity-60 italic">(척하는...)</span> 규정 알리미
                </h1>
                <p className="text-slate-500 text-xs">AI와 데이터가 흐르는 지능형 규정 안내 프로세스</p>
            </div>

            <div className="relative w-full py-4 px-2 overflow-y-auto scrollbar-hide">
                <svg className="absolute top-[45%] left-0 w-full h-12 -translate-y-1/2 z-0 pointer-events-none hidden md:block" preserveAspectRatio="none" viewBox="0 0 1000 48">
                    <path d="M 50 24 H 950" fill="transparent" stroke="#e2e8f0" strokeWidth="2" strokeDasharray="8 8" />
                    <motion.path d="M 50 24 H 950" fill="transparent" stroke="url(#flow-gradient)" strokeWidth="3" strokeLinecap="round" initial={{ pathLength: 0 }} animate={{ pathLength: (activeStep) / 3 }} transition={{ duration: 0.8, ease: "easeInOut" }} />
                    {activeStep < 3 && (
                        <motion.path d={`M ${50 + (activeStep * 300)} 24 H ${50 + ((activeStep + 1) * 300)}`} fill="transparent" stroke="white" strokeWidth="4" strokeLinecap="round" strokeDasharray="10 40" animate={{ strokeDashoffset: [-50, 0] }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} className="opacity-40" />
                    )}
                    <defs>
                        <linearGradient id="flow-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#3b82f6" />
                            <stop offset="50%" stopColor="#8b5cf6" />
                            <stop offset="100%" stopColor="#10b981" />
                        </linearGradient>
                    </defs>
                </svg>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 relative z-10">
                    {steps.map((step, idx) => {
                        const isCurrent = activeStep === idx;
                        const isSeen = activeStep >= idx;
                        const isCompleted = activeStep > idx;

                        return (
                            <motion.div
                                key={idx}
                                onClick={() => setActiveStep(idx)}
                                // [추가] DOM 요소 참조 연결
                                ref={el => stepRefs.current[idx] = el}
                                className={`relative p-5 rounded-3xl border transition-all cursor-pointer overflow-hidden flex flex-col gap-3 min-h-[220px] justify-between
                                    ${isCurrent ? 'bg-white shadow-xl scale-105 z-20 border-blue-400 ring-4 ring-blue-500/5' : 'bg-white shadow-sm border-slate-200'}
                                    ${!isSeen ? 'opacity-40 grayscale-[0.5]' : 'opacity-100 grayscale-0'}`}
                                animate={{
                                    y: isCurrent ? -8 : 0,
                                    borderColor: isCurrent ? '#3b82f6' : isCompleted ? '#10b981' : '#e2e8f0'
                                }}
                            >
                                <div className="absolute top-0 left-0 w-full h-1 bg-slate-100">
                                    <motion.div initial={{ width: 0 }} animate={{ width: isCompleted ? "100%" : isCurrent ? "60%" : "0%" }} className={`h-full ${isCompleted ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2.5 rounded-2xl ${step.color} border ${step.borderColor} transition-all duration-700 ${isCurrent ? 'rotate-[360deg] shadow-inner scale-110' : ''}`}>
                                            {step.icon}
                                        </div>
                                        <div className={`flex flex-col gap-0.5 transition-opacity duration-500 ${isSeen ? 'opacity-100' : 'opacity-0'}`}>
                                            <div className="flex items-center gap-1.5 text-[9px] font-black text-slate-400 tracking-tight uppercase">
                                                {step.tech.icon} {step.tech.label}
                                            </div>
                                            <div className={`text-[11px] font-bold tracking-tight ${isCurrent ? 'text-blue-700' : 'text-slate-600'}`}>{step.tech.name}</div>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end">
                                        <span className={`text-[9px] font-black tracking-widest ${isCurrent ? 'text-blue-600' : 'text-slate-400'}`}>STEP 0{idx + 1}</span>
                                        {isCompleted && <CheckCircle size={14} className="text-emerald-500 mt-1" />}
                                    </div>
                                </div>
                                <div className="mt-1">
                                    <h3 className={`font-bold text-base ${isSeen ? 'text-slate-900' : 'text-slate-400'}`}>{step.title}</h3>
                                    <p className={`text-[11px] font-semibold ${isCurrent ? 'text-blue-500' : isCompleted ? 'text-emerald-600' : 'text-slate-400'}`}>{step.subtitle}</p>
                                </div>
                                <div className={`transition-all duration-500 flex-1 flex flex-col justify-end ${isSeen ? 'opacity-100' : 'opacity-0'}`}>
                                    <p className="text-[10px] text-slate-500 mb-3 leading-relaxed line-clamp-2">{step.desc}</p>
                                    <div className={`rounded-xl p-3 text-[10px] border transition-colors ${isCurrent ? 'bg-blue-50/50 border-blue-100' : 'bg-slate-50 border-slate-100'}`}>
                                        {idx === 0 && <div className="flex items-center gap-1.5 text-slate-600 italic"><MousePointer2 size={10} className="text-blue-400" /> "{step.example}"</div>}
                                        {idx === 1 && <div className="space-y-1.5"><div className="flex justify-between font-bold text-indigo-500 text-[9px]"><span>RAG 엔진 데이터 스캔</span><span>92%</span></div><div className="w-full h-1 bg-slate-200 rounded-full overflow-hidden"><motion.div initial={{ width: 0 }} animate={{ width: isSeen ? "92%" : "0%" }} transition={{ duration: 2 }} className="h-full bg-indigo-500" /></div></div>}
                                        {idx === 2 && <div className="flex flex-col gap-1"><div className="flex items-center gap-1 text-purple-600 font-bold"><Sparkles size={10} /> 답변 생성 및 검증 완료</div><span className="bg-white px-1.5 py-0.5 rounded border border-purple-100 inline-block w-fit text-[9px]">근거: 여비규정 제4조 2항</span></div>}
                                        {idx === 3 && <div className="flex items-center gap-2"><FileText size={14} className="text-emerald-500 animate-bounce" /><span className="text-emerald-600 font-bold">규정 뷰어 즉시 이동</span></div>}
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            <div className="w-full mt-6 shrink-0 flex flex-col items-center gap-4">
                <div className="w-full max-w-sm bg-white/80 backdrop-blur-md rounded-full px-5 py-2.5 border border-slate-200 flex items-center justify-between shadow-sm">
                    <div className="flex gap-2">
                        {steps.map((_, i) => (
                            <button key={i} onClick={() => setActiveStep(i)} className={`h-1.5 rounded-full transition-all duration-500 ${activeStep === i ? 'w-10 bg-blue-600' : 'w-2 bg-slate-300'}`} />
                        ))}
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-[11px] font-black text-slate-400">0{activeStep + 1}</span>
                        <div className="w-px h-3.5 bg-slate-200" />
                        <span className="text-[11px] font-black text-slate-800 tracking-tight">2026 KIAST AI</span>
                    </div>
                </div>

                {!isHiddenSaved && (
                    <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 cursor-pointer group select-none">
                            <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${doNotShowAgain ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-300 group-hover:border-blue-400'}`}>
                                {doNotShowAgain && <CheckCircle size={10} className="text-white" />}
                            </div>
                            <input
                                type="checkbox"
                                className="hidden"
                                checked={doNotShowAgain}
                                onChange={(e) => setDoNotShowAgain(e.target.checked)}
                            />
                            <span className="text-xs text-slate-500 font-medium group-hover:text-slate-700 transition-colors">다음부터는 접속시 이 창을 열지 않음</span>
                        </label>
                    </div>
                )}
            </div>

            <p className="mt-2 text-[9px] text-slate-400 font-medium tracking-wide">© 2026 KIAST AI Regulation Info System. Fully Autonomous RAG Pipeline.</p>
        </div>
    );
};
// --- [Component] Tech Architecture (탭 분리 버전) ---
const TechStackModal = ({ onClose, sysInfo }) => {
    const [activeTab, setActiveTab] = useState('stack'); // 'stack' | 'arch'
    const chartRefs = useRef({});

    // Chart Data Config
    const chartData = useMemo(() => ({
        stack: {
            perf: {
                type: 'bar',
                data: {
                    labels: ['일반 키워드 검색', 'Vector 검색 (harrier-embeddings)', 'RAG + Rerank + Cache'],
                    datasets: [{
                        label: '검색 정확도',
                        data: [45, 78, 95],
                        backgroundColor: ['#cbd5e1', '#60a5fa', '#3b82f6'],
                        borderRadius: 4,
                        barThickness: 30
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                        y: { beginAtZero: true, max: 100, grid: { color: '#f1f5f9' } },
                        x: { grid: { display: false } }
                    }
                }
            },
            dist: {
                type: 'doughnut',
                data: {
                    labels: ['User Interface', 'AI & Intelligence', 'Backend & Data', 'Infrastructure'],
                    datasets: [{
                        data: [25, 30, 25, 20],
                        backgroundColor: ['#22d3ee', '#818cf8', '#fbbf24', '#f87171'],
                        borderWidth: 0,
                        hoverOffset: 10
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '60%',
                    plugins: {
                        legend: { position: 'right', labels: { usePointStyle: true, font: { size: 11 }, padding: 20 } }
                    }
                }
            }
        },
        arch: {
            latency: {
                type: 'bar',
                data: {
                    labels: [['로컬 CPU 임베딩', '(웹 서버)'], ['원격 GPU 임베딩', '(AI 서버)'], ['네트워크 전송', '오버헤드']],
                    datasets: [{
                        label: '지연 시간 (ms)',
                        data: [450, 120, 45],
                        backgroundColor: ['#3b82f6', '#8b5cf6', '#cbd5e1'],
                        borderRadius: 6,
                        barThickness: 60
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: { y: { beginAtZero: true, grid: { color: '#f1f5f9' } }, x: { grid: { display: false } } }
                }
            },
            comp: {
                type: 'doughnut',
                data: {
                    labels: ['React 프론트엔드', 'FastAPI 백엔드', '데이터베이스 (SQL/Vector)', 'AI 추론 모델', '프록시 서비스'],
                    datasets: [{
                        data: [15, 20, 35, 15, 15],
                        backgroundColor: ['#22d3ee', '#3b82f6', '#f59e0b', '#f43f5e', '#6366f1'],
                        borderWidth: 2,
                        borderColor: '#ffffff',
                        hoverOffset: 4
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '50%',
                    plugins: {
                        legend: { position: 'right', labels: { usePointStyle: true, boxWidth: 8, font: { size: 10 }, padding: 15 } }
                    }
                }
            },
            sync: {
                type: 'line',
                data: {
                    labels: ['00:00 배치', '06:00 배치', '12:00 배치', '18:00 배치'],
                    datasets: [{
                        label: '동기화된 벡터 수',
                        data: [1200, 450, 2600, 1100],
                        borderColor: '#a78bfa',
                        backgroundColor: 'rgba(167, 139, 250, 0.1)',
                        borderWidth: 2,
                        pointBackgroundColor: '#fff',
                        pointBorderColor: '#8b5cf6',
                        pointRadius: 4,
                        fill: true,
                        tension: 0.4
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                        y: { display: false },
                        x: { grid: { display: false }, ticks: { font: { size: 9 } } }
                    }
                }
            }
        }
    }), []);

    useEffect(() => {
        Object.values(chartRefs.current).forEach(chart => chart?.destroy());
        chartRefs.current = {};

        const createChart = (id, config) => {
            const ctx = document.getElementById(id);
            if (ctx) chartRefs.current[id] = new Chart(ctx, config);
        };

        if (activeTab === 'stack') {
            createChart('perfChart', chartData.stack.perf);
            createChart('distChart', chartData.stack.dist);
        } else {
            createChart('latencyChart', chartData.arch.latency);
            createChart('compChart', chartData.arch.comp);
            createChart('syncChart', chartData.arch.sync);
        }

        return () => Object.values(chartRefs.current).forEach(chart => chart?.destroy());
    }, [activeTab, chartData]);

    const renderArrow = () => (
        <div className="hidden md:flex items-center justify-center px-2">
            <ChevronRight className="text-slate-300" size={24} />
        </div>
    );

    return (
        <div className="relative w-full max-w-6xl bg-white rounded-[20px] overflow-hidden shadow-2xl flex flex-col h-[90vh]">
            {/* Header & Tabs */}
            <div className="bg-white border-b px-6 py-4 flex justify-between items-center shrink-0 z-10">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg"><Cpu className="text-blue-600" size={24} /></div>
                    <div>
                        <h2 className="text-xl font-bold text-slate-800">시스템 기술 구성도</h2>
                        <p className="text-xs text-slate-500">KIAST AI Regulation System {sysInfo?.version || 'v0.4'}</p>
                    </div>
                </div>
                <div className="flex bg-slate-100 p-1 rounded-lg">
                    <button onClick={() => setActiveTab('stack')} className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${activeTab === 'stack' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>기술 스택</button>
                    <button onClick={() => setActiveTab('arch')} className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${activeTab === 'arch' ? 'bg-white text-violet-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>아키텍처</button>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-900"><X size={20} /></button>
            </div>

            <div className="overflow-y-auto p-6 md:p-10 scrollbar-thin flex-1 bg-slate-50/50">
                {activeTab === 'stack' ? (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                        {/* [Stack] Header */}
                        <div className="text-center mb-10">
                            <h1 className="text-4xl font-black text-blue-600 mb-2">KIAST AI 규정정보시스템</h1>
                            <h2 className="text-lg font-bold text-slate-700 mb-2">RAG 기반 엔터프라이즈 지식 관리 시스템</h2>
                            <p className="text-xs text-slate-500 max-w-2xl mx-auto">React 20 기반의 직관적인 UI, 고성능 harrier-embeddings 임베딩 모델, 그리고 Azure OpenAI가 결합된 지능형 규정 검색 솔루션입니다. 데이터 파이프라인부터 인프라까지 전체 기술 구성을 시각화합니다.</p>
                        </div>

                        {/* [Stack] Pipeline */}
                        <div className="mb-8">
                            <div className="flex items-center gap-2 mb-4 border-l-4 border-blue-600 pl-3">
                                <h3 className="text-lg font-bold text-slate-800">데이터 처리 및 지능형 파이프라인</h3>
                            </div>
                            <p className="text-xs text-slate-600 mb-6">원문 데이터가 Docling 등을 통해 구조화되고, 하이브리드 RAG 엔진을 거쳐 사용자에게 전달되는 지능형 프로세스입니다.</p>

                            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                                <div className="flex flex-col md:flex-row justify-between gap-4">
                                    <div className="flex-1 bg-slate-50 border border-slate-100 rounded-xl p-5 text-center shadow-sm">
                                        <div className="text-2xl mb-3">📄</div>
                                        <div className="font-bold text-slate-800 text-sm mb-1">데이터 수집/전처리</div>
                                        <div className="text-[10px] text-blue-600 font-bold mb-2">Source Org</div>
                                        <p className="text-[10px] text-slate-500 leading-tight">HTML/PDF 원문 파싱, Docling 표 인식, 스타일 보정</p>
                                    </div>
                                    {renderArrow()}
                                    <div className="flex-1 bg-blue-50 border border-blue-100 rounded-xl p-5 text-center shadow-sm">
                                        <div className="text-2xl mb-3">🗄️</div>
                                        <div className="font-bold text-blue-900 text-sm mb-1">임베딩 및 저장</div>
                                        <div className="text-[10px] text-blue-600 font-bold mb-2">Vector Store</div>
                                        <p className="text-[10px] text-slate-500 leading-tight">ChromaDB 적재, MD5 체크, 증분 임베딩 처리</p>
                                    </div>
                                    {renderArrow()}
                                    <div className="flex-1 bg-indigo-50 border border-indigo-100 rounded-xl p-5 text-center shadow-sm">
                                        <div className="text-2xl mb-3">🧠</div>
                                        <div className="font-bold text-indigo-900 text-sm mb-1">지능형 검색 (RAG)</div>
                                        <div className="text-[10px] text-indigo-600 font-bold mb-2">Intelligence Core</div>
                                        <p className="text-[10px] text-slate-500 leading-tight">시맨틱 캐시, 계층적 확장 검색, 유사도 분석</p>
                                    </div>
                                    {renderArrow()}
                                    <div className="flex-1 bg-violet-50 border border-violet-100 rounded-xl p-5 text-center shadow-sm">
                                        <div className="text-2xl mb-3">💬</div>
                                        <div className="font-bold text-violet-900 text-sm mb-1">검증 및 응답</div>
                                        <div className="text-[10px] text-violet-600 font-bold mb-2">Azure OpenAI</div>
                                        <p className="text-[10px] text-slate-500 leading-tight">다단계 검증 루프, 근거 딥링크, SSE 스트리밍</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* [Stack] Charts */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                                <div className="mb-4 border-l-4 border-rose-500 pl-3">
                                    <h3 className="font-bold text-lg text-slate-800">검색 정확도 및 최적화 효과</h3>
                                </div>
                                <p className="text-xs text-slate-500 mb-4">Rerank 도입 및 시맨틱 캐시 적용을 통해 기존 검색 방식 대비 응답 품질과 속도가 비약적으로 향상되었습니다.</p>
                                <div className="h-64"><canvas id="perfChart"></canvas></div>
                            </div>
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                                <div className="mb-4 border-l-4 border-cyan-500 pl-3">
                                    <h3 className="font-bold text-lg text-slate-800">기술 스택 구성 분포</h3>
                                </div>
                                <p className="text-xs text-slate-500 mb-4">UI, AI 지능형 엔진, 백엔드 및 인프라가 유기적으로 결합된 시스템 아키텍처 구성입니다.</p>
                                <div className="h-64"><canvas id="distChart"></canvas></div>
                            </div>
                        </div>

                        {/* [Stack] Footer (Infrastructure) */}
                        <div className="mb-4">
                            <div className="flex items-center gap-2 mb-4 border-l-4 border-orange-500 pl-3">
                                <h3 className="text-lg font-bold text-slate-800">인프라 및 배포 환경</h3>
                            </div>
                            <p className="text-xs text-slate-600 mb-6">안정적인 서비스를 위한 Docker 컨테이너 기반의 하이브리드 배포 구조(On-premise + In-house Cloud)입니다.</p>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-white p-5 rounded-xl border border-blue-100 border-t-4 border-t-cyan-400 shadow-sm">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-1.5 bg-cyan-50 rounded"><Cpu size={16} className="text-cyan-600" /></div>
                                        <div><h4 className="font-bold text-sm text-slate-800">애플리케이션 노드</h4><p className="text-[10px] text-cyan-600 font-bold">Frontend & API Gateway</p></div>
                                    </div>
                                    <ul className="text-[11px] text-slate-600 space-y-1.5 list-disc list-inside marker:text-cyan-400">
                                        <li>React 20 + Vite Framework</li>
                                        <li>FastAPI (Python) Server</li>
                                        <li>SSE 스트리밍 응답 최적화</li>
                                    </ul>
                                </div>
                                <div className="bg-white p-5 rounded-xl border border-indigo-100 border-t-4 border-t-indigo-500 shadow-sm">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-1.5 bg-indigo-50 rounded"><Database size={16} className="text-indigo-600" /></div>
                                        <div><h4 className="font-bold text-sm text-slate-800">데이터 & 벡터 노드</h4><p className="text-[10px] text-indigo-600 font-bold">Vector DB & Storage</p></div>
                                    </div>
                                    <ul className="text-[11px] text-slate-600 space-y-1.5 list-disc list-inside marker:text-indigo-500">
                                        <li>ChromaDB 영구 저장소</li>
                                        <li>자동 CSV 데이터 파이프라인</li>
                                        <li>Strict Anchor 인젝션 최적화</li>
                                    </ul>
                                </div>
                                <div className="bg-white p-5 rounded-xl border border-purple-100 border-t-4 border-t-purple-500 shadow-sm">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-1.5 bg-purple-50 rounded"><Zap size={16} className="text-purple-600" /></div>
                                        <div><h4 className="font-bold text-sm text-slate-800">AI 컴퓨팅 노드</h4><p className="text-[10px] text-purple-600 font-bold">GPU Acceleration</p></div>
                                    </div>
                                    <ul className="text-[11px] text-slate-600 space-y-1.5 list-disc list-inside marker:text-purple-500">
                                        <li>NVIDIA RTX 4090 가속</li>
                                        <li>Docker 컨테이너화</li>
                                        <li>Local Embedding 서빙 엔진</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>

                        {/* [Arch] Header */}
                        <div className="text-center mb-12">
                            <h1 className="text-4xl font-black text-blue-600 mb-2">하이브리드 AI 아키텍처</h1>
                            <p className="text-sm text-slate-600 max-w-2xl mx-auto leading-relaxed">로컬 CPU의 효율성과 원격 GPU의 강력한 성능을 결합한 전략적 시스템입니다. 보안 프록시-웹 서비스-고성능 AI 엔진을 통과하는 데이터 흐름과 확장 가능한 임베딩 처리 방식을 활용합니다.</p>
                        </div>

                        {/* [Arch] Flow Diagram */}
                        <div className="mb-8">
                            <div className="flex items-center gap-2 mb-4 border-l-4 border-blue-500 pl-3">
                                <h3 className="text-lg font-bold text-slate-800">요청 처리 여정</h3>
                            </div>
                            <p className="text-xs text-slate-600 mb-6 leading-relaxed">
                                외부 네트워크의 사용자 트래픽은 보안을 위해 리버스 프록시를 먼저 거칩니다. 이후 시스템은 지능적으로 요청을 라우팅합니다. 단순 쿼리는 웹 서버(CPU)가 처리하고, 복잡한 벡터 임베딩 작업은 AI 서버(GPU)로 위임되어 처리 효율을 극대화합니다.
                            </p>

                            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                                    <div className="w-full md:w-48 bg-slate-50 border border-slate-200 rounded-lg p-5 text-center shadow-sm">
                                        <div className="flex justify-center mb-3 text-3xl">☁️</div>
                                        <div className="font-bold text-slate-800">외부 사용자</div>
                                        <div className="text-[11px] text-slate-500 mt-1">공용 네트워크</div>
                                    </div>
                                    {renderArrow()}
                                    <div className="w-full md:w-56 bg-blue-50 border border-blue-200 rounded-lg p-5 text-center shadow-sm relative">
                                        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full">보안 및 라우팅</div>
                                        <div className="flex justify-center mb-3 text-3xl">🛡️</div>
                                        <div className="font-bold text-blue-900">리버스 프록시</div>
                                        <div className="text-[10px] text-blue-700 mt-1 font-mono">Nginx<br />Ubuntu Server</div>
                                    </div>
                                    {renderArrow()}
                                    <div className="w-full md:w-56 bg-cyan-50 border border-cyan-200 rounded-lg p-5 text-center shadow-sm relative">
                                        <div className="absolute -top-2.5 right-2 bg-cyan-500 text-white text-[9px] font-bold px-2 py-0.5 rounded">메인 앱</div>
                                        <div className="flex justify-center mb-3 text-3xl">🖥️</div>
                                        <div className="font-bold text-cyan-900">웹 서버</div>
                                        <div className="text-[10px] text-cyan-700 mt-1 font-mono">VMware (CPU)<br />Ubuntu Server</div>
                                    </div>
                                    {renderArrow()}
                                    <div className="w-full md:w-56 bg-violet-50 border border-violet-200 rounded-lg p-5 text-center shadow-sm relative">
                                        <div className="absolute -top-2.5 right-2 bg-violet-500 text-white text-[9px] font-bold px-2 py-0.5 rounded">추론 엔진</div>
                                        <div className="flex justify-center mb-3 text-3xl">🚀</div>
                                        <div className="font-bold text-violet-900">AI 서버</div>
                                        <div className="text-[10px] text-violet-700 mt-1 font-mono">GTX 4090 (GPU)<br />Windows-WSL</div>
                                    </div>
                                </div>
                                <div className="mt-8 flex justify-center">
                                    <span className="bg-green-100 text-green-700 px-4 py-1.5 rounded-full text-xs font-bold border border-green-200">내부망 HTTP / REST 통신 방식</span>
                                </div>
                            </div>
                        </div>

                        {/* [Arch] Charts Section */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col">
                                <div className="mb-4 border-l-4 border-rose-500 pl-3">
                                    <h3 className="font-bold text-lg text-slate-800">처리 지연 시간: CPU vs GPU</h3>
                                </div>
                                <p className="text-xs text-slate-500 mb-6 leading-relaxed">임베딩 생성 시간을 비교한 결과입니다. 로컬 CPU 처리는 네트워크 오버헤드가 없으나, 원격 GPU 서버(GTX 4090)는 복잡한 모델 계산 시간을 획기적으로 단축시킵니다.</p>
                                <div className="flex-1 relative h-64 w-full"><canvas id="latencyChart"></canvas></div>
                            </div>
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col">
                                <div className="mb-4 border-l-4 border-blue-500 pl-3">
                                    <h3 className="font-bold text-lg text-slate-800">서비스 구성 분포</h3>
                                </div>
                                <p className="text-xs text-slate-500 mb-6 leading-relaxed">아키텍처 내 활성화된 서비스의 비중입니다. 각 구성 요소가 전체 시스템에서 차지하는 역할을 한눈에 파악할 수 있습니다.</p>
                                <div className="flex-1 relative h-64 w-full"><canvas id="compChart"></canvas></div>
                            </div>
                        </div>

                        {/* [Arch] Infrastructure Footer (2:1 Grid) */}
                        <div className="mb-4">
                            <div className="flex items-center gap-2 mb-4 border-l-4 border-violet-500 pl-4">
                                <h3 className="text-lg font-bold text-slate-800">인프라 및 데이터 지속성</h3>
                            </div>
                            <p className="text-xs text-slate-600 mb-6">벡터 임베딩은 GPU 서버의 마스터 ChromaDB에서 웹 서버의 로컬 ChromaDB로 배치 프로세스를 통해 동기화됩니다. 데이터의 안정적인 저장은 PostgreSQL이 실행되는 전용 데이터 서버가 담당합니다.</p>

                            <div className="flex flex-col md:flex-row gap-6">
                                {/* Left Side: Nodes Grid */}
                                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Proxy */}
                                    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-center gap-2 mb-2"><div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div><h4 className="font-bold text-sm text-slate-800">프록시 노드</h4></div>
                                        <p className="text-[10px] text-slate-400 mb-2">게이트웨이</p>
                                        <div className="space-y-1 text-xs text-slate-600">
                                            <div className="flex gap-2"><span className="font-bold min-w-[30px]">IP:</span> <span className="font-mono">192.169.20x.xxx</span></div>
                                            <div className="flex gap-2"><span className="font-bold min-w-[30px]">역할:</span> <span>리버스 프록시</span></div>
                                            <div className="flex gap-2"><span className="font-bold min-w-[30px]">SW:</span> <span>Nginx</span></div>
                                        </div>
                                    </div>
                                    {/* Web */}
                                    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-center gap-2 mb-2"><div className="w-2.5 h-2.5 rounded-full bg-cyan-500"></div><h4 className="font-bold text-sm text-slate-800">웹 노드</h4></div>
                                        <p className="text-[10px] text-slate-400 mb-2">애플리케이션 코어</p>
                                        <div className="space-y-1 text-xs text-slate-600">
                                            <div className="flex gap-2"><span className="font-bold min-w-[30px]">IP:</span> <span className="font-mono">192.169.20x.xxx</span></div>
                                            <div className="flex gap-2"><span className="font-bold min-w-[30px]">사양:</span> <span>VMware (CPU)</span></div>
                                            <div className="flex gap-2"><span className="font-bold min-w-[30px]">DB:</span> <span>로컬 ChromaDB</span></div>
                                        </div>
                                    </div>
                                    {/* AI */}
                                    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-center gap-2 mb-2"><div className="w-2.5 h-2.5 rounded-full bg-rose-500"></div><h4 className="font-bold text-sm text-slate-800">AI 노드</h4></div>
                                        <p className="text-[10px] text-slate-400 mb-2">GPU 연산 엔진</p>
                                        <div className="space-y-1 text-xs text-slate-600">
                                            <div className="flex gap-2"><span className="font-bold min-w-[30px]">IP:</span> <span className="font-mono">192.168.20x.xxx</span></div>
                                            <div className="flex gap-2"><span className="font-bold min-w-[30px]">사양:</span> <span>NVIDIA GTX 4090</span></div>
                                            <div className="flex gap-2"><span className="font-bold min-w-[30px]">모델:</span> <span>마스터 임베딩 엔진</span></div>
                                        </div>
                                    </div>
                                    {/* Data */}
                                    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-center gap-2 mb-2"><div className="w-2.5 h-2.5 rounded-full bg-indigo-500"></div><h4 className="font-bold text-sm text-slate-800">데이터 노드</h4></div>
                                        <p className="text-[10px] text-slate-400 mb-2">중앙 저장소</p>
                                        <div className="space-y-1 text-xs text-slate-600">
                                            <div className="flex gap-2"><span className="font-bold min-w-[30px]">IP:</span> <span className="font-mono">192.168.20x.xxx</span></div>
                                            <div className="flex gap-2"><span className="font-bold min-w-[30px]">DB:</span> <span>PostgreSQL</span></div>
                                            <div className="flex gap-2"><span className="font-bold min-w-[30px]">큐:</span> <span>Redpanda</span></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
};

// --- Components: Tree Item ---
const TreeItem = ({ item, level = 0, onSelect, currentUrl }) => {
    const [isOpen, setIsOpen] = useState(level === 0 || item.isOpen);
    const isFolder = item.type === 'folder';
    const isActive = currentUrl && item.url &&
        decodeURIComponent(currentUrl.split('#')[0]) === decodeURIComponent(item.url.split('#')[0]);
    const paddingLeft = level * 10 + 10;

    useEffect(() => { if (item.isOpen !== undefined) setIsOpen(item.isOpen); }, [item.isOpen]);

    const handleClick = () => {
        if (isFolder) setIsOpen(!isOpen);
        else onSelect(item);
    };

    return (
        <div className="select-none leading-snug">
            <div
                className={`flex items-center py-2 pr-2 cursor-pointer text-[13px] transition-all duration-200 border-l-4 ${isActive
                    ? 'bg-blue-50 border-blue-600 text-blue-700 font-semibold'
                    : 'border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                style={{ paddingLeft: `${paddingLeft}px` }}
                onClick={handleClick}
            >
                <div className="shrink-0 mr-1 w-4 h-4 flex items-center justify-center">
                    {isFolder ? (
                        isOpen ? <ChevronDown size={12} className="text-slate-400" /> : <ChevronRight size={12} className="text-slate-400" />
                    ) : (<span className="w-3" />)}
                </div>
                <div className="shrink-0 mr-2 w-4 h-4 flex items-center justify-center">
                    {isFolder ? (
                        isOpen ? <FolderOpen size={16} className="text-blue-600 fill-blue-50" /> : <Folder size={16} className="text-blue-500 fill-blue-50" />
                    ) : (
                        <FileText size={16} className={isActive ? 'text-blue-600 fill-blue-100' : 'text-slate-400'} />
                    )}
                </div>
                <span className="truncate">{item.title}</span>
            </div>
            {isFolder && isOpen && item.children && (
                <div className="border-l border-slate-100 ml-3">
                    {item.children.map(child => (
                        <TreeItem key={child.id} item={child} level={level + 1} onSelect={onSelect} currentUrl={currentUrl} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default function App() {
    useEffect(() => {
        let sid = sessionStorage.getItem('kiast_session_id');
        if (!sid) {
            sid = (typeof crypto !== 'undefined' && crypto.randomUUID)
                ? crypto.randomUUID()
                : 'sid_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9); // 고유 ID 생성 (Fallback)
            sessionStorage.setItem('kiast_session_id', sid);
        }
    }, []);

    return (
        <BrowserRouter>
            <Routes>
                <Route path="/" element={<ChatApp />} />
                <Route path="/admin" element={<AdminDashboard />} />
            </Routes>
        </BrowserRouter>
    );
}

// --- Main App ---
export function ChatApp() {
    const ANSWER_LENGTH_OPTIONS = [
        { value: 'concise', label: '짧게' },
        { value: 'standard', label: '보통' },
        { value: 'detailed', label: '자세히' },
    ];

    const [treeData, setTreeData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentDoc, setCurrentDoc] = useState({ title: '', url: '', id: '', versionStatus: '현행', versionDate: '' });
    const [historyData, setHistoryData] = useState([]);
    const [messages, setMessages] = useState([{ id: 1, type: 'bot', text: '안녕하세요! 항공안전기술원 AI 비서입니다. 궁금하신 규정이나 절차에 대해 물어보세요. **단! AI 답변 내용은 참고용이며, 항상 해당 규정의 원문과 소관부서의 해석을 확인해 주세요.**', timestamp: new Date() }]);
    const [input, setInput] = useState('');
    const [pendingRequests, setPendingRequests] = useState(0);
    const [answerLength, setAnswerLength] = useState('concise');
    const [isComposing, setIsComposing] = useState(false);

    // [수정] 초기값 설정 시 로컬 스토리지 확인 (기본값은 열기)
    const [isGuideOpen, setIsGuideOpen] = useState(() => {
        // 'kiast_hide_guide' 키가 'true'가 아니면 모달 열기
        return localStorage.getItem('kiast_hide_guide') !== 'true';
    });
    const [isTechOpen, setIsTechOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    const [isLeftOpen, setIsLeftOpen] = useState(window.innerWidth >= 1024);
    const [isRightOpen, setIsRightOpen] = useState(true);
    const [leftWidth, setLeftWidth] = useState(280);
    const [rightWidth, setRightWidth] = useState(400);
    const [isResizing, setIsResizing] = useState(null);
    const [bottomRatio, setBottomRatio] = useState(0.6); // 60% default for Chatbot on portrait
    const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight && window.innerWidth < 1024);
    const [isDownloadOpen, setIsDownloadOpen] = useState(false);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [sysInfo, setSysInfo] = useState({ version: '', total: 0, current: 0, notice: '' });
    const [isSystemReady, setIsSystemReady] = useState(true);

    useEffect(() => {
        const checkSystemStatus = async () => {
            try {
                const res = await fetch(`${API_BASE}/ready`);
                if (!res.ok) {
                    setIsSystemReady(false);
                } else {
                    setIsSystemReady(true);
                }
            } catch (err) {
                setIsSystemReady(false);
            }
        };

        checkSystemStatus();
        const intervalId = setInterval(checkSystemStatus, 30000);
        return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
        if (!isSystemReady) {
            setMessages(prev => {
                if (prev.length > 0 && prev[0].id === 1) {
                    const newMessages = [...prev];
                    newMessages[0] = { ...newMessages[0], text: '⚠️ **시스템 업데이트 및 정비 중입니다.**\n\n현재 챗봇 서비스가 정상 동작하지 않을 수 있습니다. 잠시 후 다시 시도해 주세요.' };
                    return newMessages;
                }
                return prev;
            });
        } else {
            setMessages(prev => {
                if (prev.length > 0 && prev[0].id === 1) {
                    const newMessages = [...prev];
                    newMessages[0] = { ...newMessages[0], text: '안녕하세요! 항공안전기술원 AI 비서입니다. 궁금하신 규정이나 절차에 대해 물어보세요. **단! AI 답변 내용은 참고용이며, 항상 해당 규정의 원문과 소관부서의 해석을 확인해 주세요.**' };
                    return newMessages;
                }
                return prev;
            });
        }
    }, [isSystemReady]);

    const downloadRef = useRef(null);
    const historyRef = useRef(null);
    const iframeRef = useRef(null);
    const chatEndRef = useRef(null);
    const containerRef = useRef(null);
    const activeHighlightCleanupRef = useRef(null);

    const syncIframeAnchor = (targetUrl) => {
        const iframe = iframeRef.current;
        if (!iframe || !targetUrl || !targetUrl.includes('#')) {
            return;
        }

        try {
            const iframeWindow = iframe.contentWindow;
            if (!iframeWindow) {
                return;
            }

            const currentBase = iframeWindow.location.href.split('#')[0];
            const targetBase = targetUrl.split('#')[0];
            if (decodeURIComponent(currentBase) !== decodeURIComponent(targetBase)) {
                return;
            }

            const hash = targetUrl.slice(targetUrl.indexOf('#'));
            const targetId = decodeURIComponent(hash.slice(1));

            if (iframeWindow.location.hash !== hash) {
                iframeWindow.location.hash = hash;
            }

            const targetEl = iframeWindow.document.getElementById(targetId)
                || iframeWindow.document.getElementById(hash.slice(1));

            if (targetEl) {
                iframeWindow.document
                    .querySelectorAll('.highlighted, [data-deeplink-highlight="true"]')
                    .forEach((el) => {
                        el.classList.remove('highlighted');
                        if (el.getAttribute('data-deeplink-highlight') === 'true') {
                            el.removeAttribute('data-deeplink-highlight');
                        }
                    });

                if (activeHighlightCleanupRef.current) {
                    activeHighlightCleanupRef.current();
                    activeHighlightCleanupRef.current = null;
                }

                const highlightEl = targetEl.closest('table') || targetEl;
                highlightEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

                if (highlightEl.tagName === 'TABLE') {
                    const previousTransition = highlightEl.style.transition;
                    const previousBoxShadow = highlightEl.style.boxShadow;
                    const previousBackgroundColor = highlightEl.style.backgroundColor;
                    const previousAnimation = highlightEl.style.animation;
                    const previousPosition = highlightEl.style.position;
                    const previousZIndex = highlightEl.style.zIndex;

                    highlightEl.setAttribute('data-deeplink-highlight', 'true');
                    highlightEl.style.transition = 'background-color 220ms ease, box-shadow 220ms ease';
                    highlightEl.style.backgroundColor = 'rgba(250, 204, 21, 0.24)';
                    highlightEl.style.boxShadow = '0 0 0 4px rgba(250, 204, 21, 0.35), 0 0 28px rgba(250, 204, 21, 0.22)';
                    highlightEl.style.animation = 'none';
                    highlightEl.style.position = previousPosition || 'relative';
                    highlightEl.style.zIndex = previousZIndex || '1';

                    const decayTimer = iframeWindow.setTimeout(() => {
                        highlightEl.style.backgroundColor = previousBackgroundColor;
                        highlightEl.style.boxShadow = previousBoxShadow;
                    }, 1200);

                    const cleanupTimer = iframeWindow.setTimeout(() => {
                        highlightEl.style.transition = previousTransition;
                        highlightEl.style.backgroundColor = previousBackgroundColor;
                        highlightEl.style.boxShadow = previousBoxShadow;
                        highlightEl.style.animation = previousAnimation;
                        highlightEl.style.position = previousPosition;
                        highlightEl.style.zIndex = previousZIndex;
                        highlightEl.removeAttribute('data-deeplink-highlight');
                        if (activeHighlightCleanupRef.current === cleanup) {
                            activeHighlightCleanupRef.current = null;
                        }
                    }, 1800);

                    const cleanup = () => {
                        iframeWindow.clearTimeout(decayTimer);
                        iframeWindow.clearTimeout(cleanupTimer);
                        highlightEl.style.transition = previousTransition;
                        highlightEl.style.backgroundColor = previousBackgroundColor;
                        highlightEl.style.boxShadow = previousBoxShadow;
                        highlightEl.style.animation = previousAnimation;
                        highlightEl.style.position = previousPosition;
                        highlightEl.style.zIndex = previousZIndex;
                        highlightEl.removeAttribute('data-deeplink-highlight');
                    };

                    activeHighlightCleanupRef.current = cleanup;
                }
            }
        } catch (err) {
            console.warn('iframe anchor sync failed', err);
        }
    };

    useEffect(() => {
        if (!currentDoc?.url || !currentDoc.url.includes('#')) return;

        const timer = window.setTimeout(() => {
            syncIframeAnchor(currentDoc.url);
        }, 150);

        return () => window.clearTimeout(timer);
    }, [currentDoc?.url]);

    const isTyping = pendingRequests > 0;

    // [Session] Helper function to get headers with session ID
    const getAuthHeaders = () => {
        const headers = { 'Content-Type': 'application/json' };
        const sid = sessionStorage.getItem('kiast_session_id');
        if (sid) headers['X-Session-ID'] = sid;
        return headers;
    };

    useEffect(() => {
        const handleResize = () => {
            const m = window.innerWidth < 1024;
            const landscape = window.innerWidth > window.innerHeight && m;
            setIsMobile(m);
            setIsLandscape(landscape);

            // 데스크톱일 경우에만 좌측 트리를 연다
            if (!m) setIsLeftOpen(true);
            else setIsLeftOpen(false);
        };
        window.addEventListener('resize', handleResize);
        fetch(`${API_BASE}/api/tree`, { headers: getAuthHeaders() })
            .then(res => res.json())
            .then(setTreeData)
            .catch(console.error);
        fetch(`${API_BASE}/api/info`, { headers: getAuthHeaders() })
            .then(res => res.json())
            .then(setSysInfo)
            .catch(console.error);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const filteredTree = useMemo(() => {
        if (!searchTerm.trim()) return treeData;
        const query = searchTerm.toLowerCase();
        const filterRec = (nodes) => nodes.map(node => {
            if (node.type === 'folder') {
                const matched = filterRec(node.children || []);
                const folderMatch = node.title.toLowerCase().includes(query);
                if (folderMatch || matched.length > 0) return { ...node, children: matched.length > 0 ? matched : node.children, isOpen: true };
            } else if (node.title.toLowerCase().includes(query)) return node;
            return null;
        }).filter(Boolean);
        return filterRec(treeData);
    }, [searchTerm, treeData]);

    const handleGoHome = () => {
        setCurrentDoc({ title: '', url: '', id: '', versionStatus: '현행', versionDate: '' });
        setSearchTerm(''); // 검색어도 초기화 (선택 사항)
    };

    const fetchHistory = async (code) => {
        if (!code) return;
        try {
            const res = await fetch(`${API_BASE}/api/history/${code}`, { headers: getAuthHeaders() });
            const data = await res.json();
            setHistoryData(Array.isArray(data) ? data : []);
        } catch (e) { setHistoryData([]); }
    };

    const loadRegulation = async (item, targetUrl = null) => {
        const urlToUse = targetUrl || (item.url.startsWith('http') ? item.url : `${API_BASE}${item.url}`);
        setCurrentDoc({ title: item.title, url: urlToUse, id: item.id, versionStatus: '현행', versionDate: '' });
        fetchHistory(item.id);
        if (iframeRef.current) iframeRef.current.src = urlToUse;
    };

    // [복구] 트리 역검색 함수 (딥링크 Fallback용)
    const findRegulationInTree = (nodes, targetUrl) => {
        if (!nodes || !targetUrl) return null;
        const decodedUrl = decodeURIComponent(targetUrl);
        const fileName = decodedUrl.split('/').pop().split('#')[0].split('?')[0];
        const codeMatch = fileName.match(/^(\d+[-_]\d+)/);
        const targetCode = codeMatch ? codeMatch[1].replace('_', '-') : null;

        const search = (items) => {
            for (const item of items) {
                if (item.type === 'file') {
                    const itemFileName = decodeURIComponent(item.url || "").split('/').pop().split('?')[0];
                    if (item.id === targetCode || itemFileName === fileName) return item;
                }
                if (item.children) { const found = search(item.children); if (found) return found; }
            }
            return null;
        };
        return search(nodes);
    };

    // [중요] 이동 및 검색 로직 (강력한 매칭)
    const navigateToRegulation = (regName, article) => {
        if (!treeData || treeData.length === 0) {
            alert("규정 목록을 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
            return;
        }

        // [핵심 로직 1] 검색어 정규화 (공백 제거)
        const targetName = regName.replace(/\s+/g, '').trim();

        // 트리 재귀 검색
        const findNode = (nodes) => {
            for (const node of nodes) {
                // 폴더가 아닌 파일만 비교
                if (node.type !== 'folder') {
                    // [핵심 로직 2] 트리 노드 제목 정규화
                    const nodeTitlePure = node.title
                        .replace(/^\[[^\]]+\]\s*/, '') // [코드] 제거
                        .replace(/^\d+[-._]\d+\s*/, '') // 숫자 패턴 제거
                        .replace(/\s+/g, ''); // 공백 제거

                    // [핵심 로직 3] 완전 일치 비교
                    if (nodeTitlePure === targetName) {
                        return node;
                    }
                }

                if (node.children) {
                    const found = findNode(node.children);
                    if (found) return found;
                }
            }
            return null;
        };

        const targetNode = findNode(treeData);

        if (!targetNode) {
            console.warn(`❌ 규정을 찾을 수 없음. 검색어: ${targetName}`);
            alert(`'${regName}' 규정을 찾을 수 없습니다.\n규정명이 정확한지 확인해주세요.`);
            return;
        }

        // ---------------------------------------------------------
        // [수정됨] 앵커 생성 로직 (가지번호 지원)
        // ---------------------------------------------------------
        let anchor = "";
        const cleanArt = article.replace(/\s/g, ''); // 공백 제거

        if (cleanArt.includes("별표") || cleanArt.includes("별지") || cleanArt.includes("서식")) {
            // 정규식: 별표/별지/서식 + 숫자(Group 1) + (선택: 의 + 숫자(Group 2))
            // 예: "별표3의5" -> match[1]="3", match[2]="5"
            const match = cleanArt.match(/(?:별표|별지|서식)제?(\d+)(?:호)?(?:의(\d+))?/);
            const prefix = cleanArt.includes("별표") ? "star" : "form";
            const hasAppendixKeyword = cleanArt.includes("별지") || cleanArt.includes("서식");

            if (match) {
                const mainNum = match[1];
                const subNum = match[2]; // 가지번호 (없으면 undefined)

                if (subNum) {
                    // [별표 3의5] -> #attached_star_3_5
                    anchor = `#attached_${prefix}_${mainNum}_${subNum}`;
                } else {
                    // [별표 3] -> #attached_star_3
                    anchor = `#attached_${prefix}_${mainNum}`;
                }
            } else {
                // 번호 없는 경우 (예: [별표])
                anchor = hasAppendixKeyword ? `#attached_form` : `#attached_${prefix}`;
            }
        } else {
            // [수정된 정규식]
            // 제20조 -> match[1]: 20, match[2]: undefined
            // 제20조의2 -> match[1]: 20, match[2]: 2
            const match = cleanArt.match(/제(\d+)조(?:의(\d+))?/);

            if (match) {
                const mainNum = match[1];
                const subNum = match[2];

                if (subNum) {
                    // Python 스크립트 규칙(article_20_2)과 일치시킴
                    anchor = `#article_${mainNum}_${subNum}`;
                } else {
                    anchor = `#article_${mainNum}`;
                }
            }
        }

        const baseUrl = targetNode.url.startsWith('http') ? targetNode.url : `${API_BASE}${targetNode.url}`;
        const finalUrl = `${baseUrl.split('#')[0]}${anchor}`;

        // 뷰어 상태 업데이트
        setCurrentDoc({
            title: `${targetNode.title} ${article}`,
            url: finalUrl,
            id: targetNode.id,
            versionStatus: '현행',
            versionDate: ''
        });

        fetchHistory(targetNode.id);

        if (iframeRef.current) iframeRef.current.src = finalUrl;
        // if (isMobile) setIsRightOpen(false);
    };

    const handleSelectRegulation = (item) => {
        loadRegulation(item);
        if (isMobile) setIsLeftOpen(false);
    };

    const handleSelectHistory = (h) => {
        const fullUrl = h.url.startsWith('http') ? h.url : `${API_BASE}${h.url}`;
        setCurrentDoc(prev => ({ ...prev, url: fullUrl, versionStatus: h.status, versionDate: h.display_date }));
        if (iframeRef.current) iframeRef.current.src = fullUrl;
        setIsHistoryOpen(false);
    };

    const handleDeepLink = (e, href, label) => {
        e.preventDefault();

        if (href.includes('action_search')) {
            try {
                const urlObj = new URL(href.startsWith('http') ? href : `http://dummy/${href}`);
                const regName = decodeURIComponent(urlObj.searchParams.get('reg') || "");
                const clause = decodeURIComponent(urlObj.searchParams.get('clause') || "");
                if (regName) navigateToRegulation(regName, clause);
            } catch (err) { console.error(err); }
            return;
        }

        if (href.includes('/data')) {
            const fullUrl = href.startsWith('http') ? href : `${API_BASE}${href}`;
            const urlObj = new URL(fullUrl);
            const injectedCode = urlObj.searchParams.get('reg_code');
            const injectedName = urlObj.searchParams.get('reg_name');
            const clauseText = getPlainText(label).replace(/^[\[【]|[\]】]$/g, '').trim();

            if (injectedName && injectedCode) {
                const combined = (injectedName + " " + clauseText).trim();
                setCurrentDoc({ title: combined, url: fullUrl, id: injectedCode, versionStatus: '현행', versionDate: '' });
                fetchHistory(injectedCode);
            } else {
                const matched = findRegulationInTree(treeData, href);
                if (matched) {
                    const combined = (matched.title + " " + clauseText).trim();
                    setCurrentDoc({ title: combined, url: fullUrl, id: matched.id, versionStatus: '현행', versionDate: '' });
                    fetchHistory(matched.id);
                } else {
                    setCurrentDoc(prev => ({ ...prev, title: clauseText || "규정 문서", url: fullUrl, id: '' }));
                }
            }
            if (iframeRef.current) iframeRef.current.src = fullUrl;
            if (isMobile) setIsRightOpen(false);
        }
    };

    const handleDownload = async (ext) => {
        if (!currentDoc.url) return;
        const cleanUrl = currentDoc.url.split('#')[0].split('?')[0];
        const urlParts = cleanUrl.split('/');
        const targetFileName = urlParts[urlParts.length - 1].replace('.html', `.${ext}`);
        const dataIdx = urlParts.indexOf('data');
        let targetUrl = (dataIdx !== -1 && urlParts.length > dataIdx + 2)
            ? urlParts.slice(0, dataIdx + 1).join('/') + '/' + targetFileName
            : cleanUrl.replace('.html', `.${ext}`);

        try {
            const res = await fetch(targetUrl, { headers: getAuthHeaders() });
            const blob = await res.blob();
            const bUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = bUrl; link.setAttribute('download', decodeURIComponent(targetFileName));
            document.body.appendChild(link); link.click(); document.body.removeChild(link);
            window.URL.revokeObjectURL(bUrl);
        } catch (err) { window.open(targetUrl, '_blank'); }
        setIsDownloadOpen(false);
    };

    const handleSendMessage = async (e) => {
        e?.preventDefault?.();
        if (isComposing || pendingRequests > 0) return;
        const trimmedInput = input.trim();
        if (!trimmedInput) return;
        const userText = trimmedInput;
        const userMsgId = Date.now();
        const botMsgId = userMsgId + 1;
        const messageSnapshot = [...messages, { id: userMsgId, type: 'user', text: userText, timestamp: new Date() }];
        setMessages(prev => [...prev, { id: userMsgId, type: 'user', text: userText, timestamp: new Date() }, { id: botMsgId, type: 'bot', text: '🔍 규정 검색 중...', timestamp: new Date(), status: 'searching' }]);
        setInput('');
        setPendingRequests(prev => prev + 1);

        const chatHistory = [];
        for (let i = 1; i < messageSnapshot.length; i += 2) {
            if (messageSnapshot[i] && messageSnapshot[i + 1]?.text) chatHistory.push([messageSnapshot[i].text, messageSnapshot[i + 1].text]);
        }

        const sid = sessionStorage.getItem('kiast_session_id');
        try {
            const res = await fetch(`${API_BASE}/api/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Session-ID': sid
                },
                body: JSON.stringify({
                    session_id: sid,
                    query: userText,
                    history: chatHistory,
                    answer_length: answerLength
                })
            });
            if (!res.ok || !res.body) throw new Error('chat request failed');
            const reader = res.body.getReader();
            const decoder = new TextDecoder();

            let remainder = "";
            let accumulatedText = "";
            const updateBotMessage = (status = 'streaming') => {
                setMessages(prev => {
                    const newMsgs = [...prev];
                    const idx = newMsgs.findIndex(m => m.id === botMsgId);
                    if (idx !== -1) newMsgs[idx] = { ...newMsgs[idx], text: accumulatedText, status };
                    return newMsgs;
                });
            };
            while (true) {
                const { value, done } = await reader.read();
                if (done) {
                    const finalLine = remainder.trim();
                    if (finalLine.startsWith('data: ')) {
                        const dataStr = finalLine.slice(6).trim();
                        if (dataStr && dataStr !== '[DONE]') {
                            try {
                                const data = JSON.parse(dataStr);
                                if (typeof data.text === 'string') {
                                    accumulatedText += data.text;
                                    updateBotMessage();
                                }
                            } catch (err) {
                                console.warn('Failed to parse trailing SSE chunk', err);
                            }
                        }
                    }
                    break;
                }
                const lines = (remainder + decoder.decode(value, { stream: true })).split('\n');
                remainder = lines.pop();
                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const dataStr = line.slice(6).trim();
                        if (dataStr === '[DONE]') {
                            updateBotMessage('done');
                            return;
                        }
                        try {
                            const data = JSON.parse(dataStr);
                            if (typeof data.text === 'string') {
                                accumulatedText += data.text;
                            }
                            updateBotMessage();
                        } catch (err) { remainder = line; }
                    }
                }
            }
        } catch (error) {
            setMessages(prev => {
                const newMsgs = [...prev];
                const idx = newMsgs.findIndex(m => m.id === botMsgId);
                if (idx !== -1) {
                    newMsgs[idx] = { ...newMsgs[idx], text: '오류가 발생했습니다.', status: 'error' };
                    return newMsgs;
                }
                return [...prev, { id: Date.now(), type: 'bot', text: "오류가 발생했습니다.", timestamp: new Date(), status: 'error' }];
            });
        } finally {
            setPendingRequests(prev => Math.max(0, prev - 1));
        }
    };

    // removed duplicate state declarations

    const startResizing = (dir) => setIsResizing(dir);
    const stopResizing = () => setIsResizing(null);
    const resize = useCallback((e) => {
        if (!isResizing || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        if (isResizing === 'left') {
            const val = clientX - rect.left;
            if (val > 180 && val < 500) setLeftWidth(val);
        } else if (isResizing === 'right') {
            const val = rect.right - clientX;
            if (val > 300 && val < 700) setRightWidth(val);
        } else if (isResizing === 'bottom') {
            const ratio = (rect.bottom - clientY) / rect.height;
            if (ratio > 0.2 && ratio < 0.8) setBottomRatio(ratio);
        }
    }, [isResizing]);

    useEffect(() => {
        window.addEventListener('mousemove', resize);
        window.addEventListener('mouseup', stopResizing);
        window.addEventListener('touchmove', resize, { passive: false });
        window.addEventListener('touchend', stopResizing);
        return () => {
            window.removeEventListener('mousemove', resize);
            window.removeEventListener('mouseup', stopResizing);
            window.removeEventListener('touchmove', resize);
            window.removeEventListener('touchend', stopResizing);
        };
    }, [resize]);

    useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, isTyping]);

    useEffect(() => {
        const close = (e) => {
            if (downloadRef.current && !downloadRef.current.contains(e.target)) setIsDownloadOpen(false);
            if (historyRef.current && !historyRef.current.contains(e.target)) setIsHistoryOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    return (
        <div className="flex flex-col h-[100dvh] bg-slate-50 font-sans text-slate-900 overflow-hidden" translate="no">
            {/* Header */}
            <header className="h-12 bg-[#1e3a8a] text-white flex items-center justify-between px-4 shadow-md z-50 shrink-0">
                <div className="flex items-center gap-3">
                    <button onClick={() => setIsLeftOpen(!isLeftOpen)} className="p-1 hover:bg-white/10 rounded transition-colors">{isMobile ? <Menu size={20} /> : <PanelLeft size={18} />}</button>
                    <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-white/10 rounded flex items-center justify-center font-bold text-xs cursor-pointer" onClick={handleGoHome}> K</div>
                        <h1 className="text-sm font-bold tracking-tight">KIAST AI 규정정보시스템<span className="text-white/80"> by Icarus@경량인증실</span></h1>
                    </div>
                </div>
                <div className="flex items-center gap-1.5">
                    <button onClick={() => setIsGuideOpen(true)} className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-white/80" title="이용 가이드"><HelpCircle size={20} /></button>
                    <button onClick={() => setIsTechOpen(true)} className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-white/80" title="기술 구성도"><InfoIcon size={20} /></button>
                    <button onClick={() => setIsRightOpen(!isRightOpen)} className={`p-1.5 rounded transition-colors flex items-center gap-1 ${isRightOpen ? 'bg-white/20' : 'hover:bg-white/10'}`}><Bot size={18} /><span className="text-xs hidden sm:inline">{isRightOpen ? '챗봇 닫기' : 'AI 비서'}</span></button>
                </div>
            </header>

            <div ref={containerRef} className={`flex-1 flex overflow-hidden relative ${(!isMobile || isLandscape) ? 'flex-row' : 'flex-col'}`}>
                {/* 1. Sidebar */}
                <aside className={`bg-white border-r border-slate-200 flex flex-col z-40 transition-all duration-300 ${isMobile ? 'absolute inset-y-0 left-0 shadow-2xl' : 'relative'}`}
                    style={{
                        width: isMobile ? '280px' : `${isLeftOpen ? leftWidth : 0}px`,
                        transform: (isMobile && !isLeftOpen) ? 'translateX(-100%)' : 'none',
                        display: (!isMobile && !isLeftOpen) ? 'none' : 'flex'
                    }}>
                    <div className="p-3 border-b bg-slate-50 flex justify-between items-center shrink-0">
                        <div className="flex items-center gap-2 text-slate-700"><List size={16} /><span className="font-bold text-xs uppercase tracking-wider">규정 목록</span>
                            <span className="text-[11px] text-slate-500 font-medium sm:inline-block">
                                {sysInfo.total > 0 && ` 총 ${sysInfo.total}건`}
                                {sysInfo.current > 0 && ` (현행 ${sysInfo.current}건)`}
                            </span></div>
                        {isMobile && <button onClick={() => setIsLeftOpen(false)}><X size={14} /></button>}
                    </div>
                    <div className="p-2 border-b"><div className="relative"><input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="규정 검색..." className="w-full pl-7 pr-8 py-1.5 bg-slate-50 border-none rounded-sm text-xs focus:ring-1 focus:ring-blue-500" /><Search size={10} className="absolute left-2 top-2.5 text-slate-400" />{searchTerm && <X size={10} className="absolute right-2 top-2.5 cursor-pointer text-slate-400" onClick={() => setSearchTerm('')} />}</div></div>
                    <div className="flex-1 overflow-y-auto scrollbar-thin">{filteredTree.map(i => <TreeItem key={i.id} item={i} onSelect={handleSelectRegulation} currentUrl={currentDoc.url} />)}</div>
                </aside>

                {!isMobile && isLeftOpen && <div className="w-1 cursor-col-resize hover:bg-blue-400 z-20" onMouseDown={() => startResizing('left')} />}
                {isMobile && isLeftOpen && <div className="absolute inset-0 bg-black/20 z-30" onClick={() => setIsLeftOpen(false)} />}

                {/* 2. Viewer */}
                <section className={`flex-1 flex flex-col min-w-0 min-h-0 relative z-0 transition-colors ${currentDoc.versionStatus === '과거' ? 'bg-amber-50/20' : 'bg-[#f1f5f9]'}`}>
                    <div className="h-9 bg-white border-b border-slate-200 px-3 flex items-center justify-between shadow-sm shrink-0">
                        <div className="flex items-center gap-2 overflow-hidden">
                            <FileText size={14} className={currentDoc.versionStatus === '과거' ? 'text-amber-500' : 'text-blue-600'} />
                            <span className={`text-xs font-bold truncate ${currentDoc.versionStatus === '과거' ? 'text-amber-700' : 'text-slate-700'}`}>
                                {currentDoc.url ? <>{currentDoc.versionStatus === '과거' && "[과거 규정] "}{currentDoc.versionStatus === '예정' && "[시행 예정] "}{currentDoc.title}{currentDoc.versionDate && <span className="ml-1 opacity-70">({currentDoc.versionDate})</span>}</> : "규정을 선택해주세요"}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            {currentDoc.id && <div className="relative" ref={historyRef}><button onClick={() => setIsHistoryOpen(!isHistoryOpen)} className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border border-slate-200 text-slate-600 hover:bg-slate-50"><History size={10} /> 연혁 ({historyData.length}) <ChevronDown size={8} /></button>{isHistoryOpen && <div className="absolute top-8 right-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-50"><div className="px-3 py-1 text-[9px] font-bold text-slate-400 border-b border-slate-50 uppercase">개정 이력</div><div className="max-h-80 overflow-y-auto scrollbar-thin">{Array.isArray(historyData) && historyData.map((h, i) => <button key={i} onClick={() => handleSelectHistory(h)} className="w-full text-left px-3 py-2 hover:bg-blue-50 flex flex-col gap-0.5 border-b border-slate-50 last:border-0 transition-colors"><div className="flex justify-between items-center"><div className="flex items-center gap-1.5"><span className="text-xs font-bold text-slate-700">{h.display_date}</span>{h.status === '현행' && <span className="text-[9px] font-extrabold text-blue-600 bg-blue-50 px-1 rounded border border-blue-100">[현행]</span>}{h.status === '예정' && <span className="text-[9px] font-extrabold text-orange-600 bg-orange-50 px-1 rounded border border-orange-100">[예정]</span>}</div><span className={`text-[9px] font-bold ${h.status === '현행' ? 'text-green-600' : 'text-slate-400'}`}>{h.status === '현행' ? '유효' : '이전'}</span></div><span className="text-[9px] text-slate-400 truncate">{h.filename}</span></button>)}</div></div>}</div>}
                            {currentDoc.url && <div className="flex items-center gap-1" ref={downloadRef}><button onClick={() => setIsDownloadOpen(!isDownloadOpen)} className="flex items-center gap-1 px-2 py-0.5 hover:bg-slate-100 rounded text-slate-600 text-[10px] font-medium border border-slate-200"><Download size={10} /> 다운로드 <ChevronDown size={8} /></button>{isDownloadOpen && <div className="absolute top-8 right-0 mt-1 w-28 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50"><button onClick={() => handleDownload('hwpx')} className="w-full text-left px-3 py-1.5 hover:bg-slate-50 block text-xs">HWPX 저장</button><button onClick={() => handleDownload('pdf')} className="w-full text-left px-3 py-1.5 hover:bg-slate-50 block text-xs">PDF 저장</button></div>}</div>}
                            <button className="p-1 hover:bg-slate-100 rounded text-slate-500" onClick={() => window.open(currentDoc.url, '_blank')}><ExternalLink size={12} /></button>
                        </div>
                    </div>
                    <div className="flex-1 bg-white relative overflow-hidden min-h-0 flex flex-col">
                        {currentDoc.url ? (
                            <iframe key={currentDoc.url.split('#')[0].split('?')[0]} ref={iframeRef} src={currentDoc.url} onLoad={() => syncIframeAnchor(currentDoc.url)} className="w-full h-full border-none" title="Viewer" />
                        ) : (
                            // [수정] 기존 placeholder div 대신 WelcomeScreen 컴포넌트 렌더링
                            <WelcomeScreen sysInfo={sysInfo} isSystemReady={isSystemReady} />
                        )}
                        {isResizing && <div className="absolute inset-0 z-50 bg-transparent" />}
                    </div>
                </section>

                {!isMobile && isRightOpen && <div className="w-1 cursor-col-resize hover:bg-blue-400 z-20" onMouseDown={() => startResizing('right')} />}

                {/* 3. Chatbot */}
                <aside
                    className={`bg-white shadow-xl z-20 transition-all duration-300 flex flex-col shrink-0 relative
                    ${isMobile
                            ? (isLandscape
                                ? (isRightOpen ? 'h-full border-l border-slate-200 flex-1' : 'w-0 border-none overflow-hidden')
                                : (isRightOpen ? 'border-t border-slate-200 w-full' : 'h-0 w-full border-none overflow-hidden')
                            )
                            : (isRightOpen ? 'border-l border-slate-200 h-full' : 'h-full border-none overflow-hidden')
                        }`}
                    style={{
                        // [핵심 수정] 닫혔을 때는 width를 0으로 설정하여 공간을 반납하게 함
                        width: isMobile ? (isLandscape ? (isRightOpen ? 'auto' : '0px') : '100%') : (isRightOpen ? `${rightWidth}px` : '0px'),
                        height: isMobile ? (isLandscape ? '100%' : (isRightOpen ? `${bottomRatio * 100}dvh` : '0px')) : '100%',
                    }}
                >
                    {isMobile && isRightOpen && !isLandscape && (
                        <div
                            className="absolute -top-3 left-0 right-0 h-6 cursor-row-resize flex items-center justify-center z-50 group"
                            onMouseDown={(e) => { e.preventDefault(); startResizing('bottom'); }}
                            onTouchStart={(e) => startResizing('bottom')}
                        >
                            <div className="w-12 h-1 bg-slate-300 rounded-full group-hover:bg-blue-400 transition-colors" />
                        </div>
                    )}
                    <div className="p-2 border-b border-slate-100 bg-slate-50 flex items-center justify-between px-3 shrink-0">
                        <div className="flex items-center gap-2">
                            <Bot size={16} className="text-blue-600" />
                            <h3 className="text-xs font-bold text-slate-800">AI 규정 비서</h3>
                            {pendingRequests > 0 && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-[10px] font-bold text-blue-600">처리중 {pendingRequests}</span>}
                        </div>
                        <button onClick={() => setIsRightOpen(false)} className="p-1 hover:bg-slate-200 rounded">
                            <X size={16} />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/30 scrollbar-thin">
                        {messages.map((msg) => (
                            <div key={msg.id} className={`flex flex-col ${msg.type === 'user' ? 'items-end' : 'items-start'}`}>
                                <div className={`flex gap-3 max-w-[95%] ${msg.type === 'user' ? 'flex-row-reverse' : ''}`}>
                                    <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center border mt-1 ${msg.type === 'user' ? 'bg-blue-600 text-white' : 'bg-white text-blue-600'}`}>{msg.type === 'user' ? <User size={16} /> : <Bot size={16} />}</div>
                                    <div className="flex flex-col gap-1">
                                        <div className={`p-3 rounded-2xl text-[13px] leading-relaxed shadow-sm ${msg.type === 'user' ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-white text-slate-800 border border-slate-200 rounded-tl-none'}`}>
                                            {msg.type === 'user' ? msg.text : (
                                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                                                    a: ({ node, href, children }) => {
                                                        const isDeepLink = href && (href.includes('/data') || href.includes('action_search'));
                                                        if (isDeepLink) {
                                                            return (
                                                                <span onClick={(e) => handleDeepLink(e, href, children)} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-600 text-white font-bold cursor-pointer hover:bg-blue-700 mx-1 text-[11px] align-baseline shadow-sm"><ExternalLink size={10} />{children}</span>
                                                            );
                                                        }
                                                        return <a href={href} target="_blank" rel="noreferrer" className="text-blue-600 underline">{children}</a>;
                                                    },
                                                    strong: ({ node, ...props }) => <span className="font-bold text-blue-700 bg-blue-50 px-1 rounded" {...props} />
                                                }}>{preprocessMessage(msg.text)}</ReactMarkdown>
                                            )}
                                        </div>
                                        <span className={`text-[10px] text-slate-400 ${msg.type === 'user' ? 'text-right' : 'text-left'}`}>{formatTime(msg.timestamp)}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div ref={chatEndRef} />
                    </div>

                    <div className="p-3 bg-white border-t border-slate-100 shrink-0">
                        <div className="mb-2 flex items-center justify-between gap-2 px-1">
                            <span className="text-[10px] font-semibold text-slate-500">답변 길이</span>
                            <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
                                {ANSWER_LENGTH_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => setAnswerLength(option.value)}
                                        className={`rounded-md px-2 py-1 text-[10px] font-semibold transition-colors ${answerLength === option.value
                                            ? 'bg-white text-blue-600 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700'
                                            }`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <form onSubmit={handleSendMessage} className="relative shadow-sm rounded-lg overflow-hidden border border-slate-300 focus-within:border-blue-500 transition-all">
                            <textarea rows="1" placeholder={isSystemReady === false ? "시스템 점검 중입니다..." : "규정, 절차, 양식 무엇이든 물어보세요."} className="w-full pl-3 pr-10 py-2.5 bg-white text-xs focus:outline-none resize-none disabled:bg-slate-50 disabled:text-slate-400" value={input} disabled={pendingRequests > 0 || isSystemReady === false} onChange={(e) => setInput(e.target.value)} onCompositionStart={() => setIsComposing(true)} onCompositionEnd={(e) => { setIsComposing(false); setInput(e.target.value); }} onKeyDown={(e) => { if (e.nativeEvent?.isComposing || isComposing) return; if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(e); } }} />
                            <button type="submit" disabled={!input.trim() || pendingRequests > 0 || isComposing || isSystemReady === false} className="absolute right-1.5 bottom-1.5 p-1.5 rounded bg-slate-100 text-slate-400 hover:bg-blue-600 hover:text-white transition-colors disabled:cursor-not-allowed disabled:hover:bg-slate-100 disabled:hover:text-slate-400"><Send size={16} /></button>
                        </form>
                        {pendingRequests > 0 && <p className="mt-2 text-[10px] text-center text-blue-600 font-medium">답변 생성 중입니다. 완료 후 다음 질문을 입력해주세요.</p>}
                        <p className="text-[10px] text-slate-400 text-center mt-2 px-2 break-keep">AI 답변은 참고용으로 규정 원문 및 소관 부서의 해석을 확인하시기 바랍니다.</p>
                    </div>
                </aside>
            </div>
            <footer className="h-7 bg-slate-50 border-t border-slate-200 flex items-center justify-between px-4 text-[9px] text-slate-400 shrink-0 z-50"><div>© 2026 KIAST AI 규정정보시스템{sysInfo.version && `(${sysInfo.version})`}. All Rights Reserved.</div><div className="flex gap-4"><span>개인정보처리방침</span><span>이용약관</span></div></footer>

            <AnimatePresence>
                {isGuideOpen && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" onClick={() => setIsGuideOpen(false)}><motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-5xl"> <SystemGuide onClose={() => setIsGuideOpen(false)} sysInfo={sysInfo} /></motion.div></motion.div>}
                {isTechOpen && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md" onClick={() => setIsTechOpen(false)}>
                        <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-6xl">
                            <TechStackModal onClose={() => setIsTechOpen(false)} sysInfo={sysInfo} />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}